import type { ObservationRow, Observations, Tenant } from './api';
import { settingsBoundary } from './settings-model';

export type InspectorPage = { tenant: Tenant; findings: ObservationRow[]; nextCursor: string | null; revision: string };

/** Publish only a complete, single-revision result; failed/cancelled pages never become a partial list. */
export async function loadInspectorFindings(base: Observations, fetchPage: (cursor?: string) => Promise<InspectorPage>, signal?: AbortSignal): Promise<Observations> {
  const findings = new Map(base.accounts.map(account => [account.accountId, [] as ObservationRow[]]));
  const cursors = new Set<string>(), ids = new Set<string>();
  let cursor: string | undefined, revision: string | undefined;
  do {
    signal?.throwIfAborted();
    const page = await fetchPage(cursor);
    signal?.throwIfAborted();
    if (!page || settingsBoundary(page.tenant) !== settingsBoundary(base.tenant) || !Array.isArray(page.findings)
      || typeof page.revision !== 'string' || !page.revision || (revision && revision !== page.revision)
      || !(page.nextCursor === null || typeof page.nextCursor === 'string' && page.nextCursor.length > 0)) throw new Error('Inspectorの収集データが変更されました。表示を更新してください。');
    revision = page.revision;
    for (const row of page.findings) {
      if (!row || typeof row.findingArn !== 'string' || typeof row.awsAccountId !== 'string' || !findings.has(row.awsAccountId)) throw new Error('Inspectorのアカウント情報を確認できません。');
      const id = `${row.awsAccountId}|${row.findingArn}`;
      if (ids.has(id)) throw new Error('Inspectorのページが重複しています。表示を更新してください。');
      ids.add(id); findings.get(row.awsAccountId)!.push(row);
    }
    if (page.nextCursor && cursors.has(page.nextCursor)) throw new Error('Inspectorのページが循環しています。表示を更新してください。');
    cursor = page.nextCursor ?? undefined;
    if (cursor) cursors.add(cursor);
  } while (cursor);
  for (const account of base.accounts) {
    const expected = account.inspector?.findingCount;
    if (typeof expected === 'number' && expected !== findings.get(account.accountId)!.length) throw new Error('Inspectorの件数が収集結果と一致しません。表示を更新してください。');
  }
  return { ...base, accounts: base.accounts.map(account => ({ ...account, inspector: {
    ...(account.inspector ?? { status: 'NOT_COLLECTED', checkedAt: null }),
    findings: findings.get(account.accountId)!, findingsLoaded: true,
  } })) };
}

export function inspectorMetadataKey(value: Observations): string {
  const canonical = (item: unknown): unknown => Array.isArray(item) ? item.map(canonical) : item && typeof item === 'object'
    ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)).map(([key, entry]) => [key, canonical(entry)])) : item;
  return JSON.stringify(canonical([value.tenant, value.accounts.map(account => [account.accountId, account.inspector])]));
}
